import { useState, useRef, useEffect } from 'react'
import { themes, useBrand } from '../../context/BrandContext'

export default function ClientConfig() {
  const [companyName, setCompanyName] = useState('Demo Company Co.')
  const [monogram, setMonogram] = useState('DC')
  const [color, setColor] = useState('#B8734A')
  const [sidebarColor, setSidebarColor] = useState('#2B2B2B')
  const [bgColor, setBgColor] = useState('#F7F2EB')
  const [textColor, setTextColor] = useState('#2B2B2B')
  const [navTextColor, setNavTextColor] = useState('#FFFFFF')
  const [logoSize, setLogoSize] = useState('medium')
  const [tagline, setTagline] = useState('Simplifying Work. Amplifying Results.')
  const [industry, setIndustry] = useState('General Business')
  const [departments, setDepartments] = useState(['Operations','Administrative','Field Staff','Customer Service'])
  const [newDept, setNewDept] = useState('')
  const [saved, setSaved] = useState(false)
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [documents, setDocuments] = useState([])
  const [videos, setVideos] = useState([])
  const [videoUrl, setVideoUrl] = useState('')
  const [videoTitle, setVideoTitle] = useState('')
  const [timeOffCategories, setTimeOffCategories] = useState(['Vacation','Sick','Personal','Unpaid'])
  const [newCategory, setNewCategory] = useState('')
  const logoRef = useRef()
  const docRef = useRef()
  const videoRef = useRef()

  const [features, setFeatures] = useState({
    onboarding: true, policies: true, timesheets: true,
    timeOff: true, memos: true, incidents: true,
    performance: true, demoMode: false,
  })

  const presets = [
    '#B8734A','#4A6B8A','#4A7C59','#6B7D6B',
    '#C4843A','#A0392B','#2B6B6B','#5A5A7A',
  ]

  function toggleFeature(key) { setFeatures(f => ({...f, [key]: !f[key]})) }
  function addDept() { if (!newDept.trim()) return; setDepartments(d => [...d, newDept.trim()]); setNewDept('') }
  function removeDept(i) { setDepartments(d => d.filter((_,idx) => idx !== i)) }
  function addCategory() { if (!newCategory.trim()) return; setTimeOffCategories(c => [...c, newCategory.trim()]); setNewCategory('') }
  function removeCategory(i) { setTimeOffCategories(c => c.filter((_,idx) => idx !== i)) }
  function handleSave() { handleSaveWithTheme() }
  function handleReset() {
    setColor('#B8734A')
    setSidebarColor('#2B2B2B')
    setBgColor('#F7F2EB')
    setTextColor('#2B2B2B')
    setNavTextColor('#FFFFFF')
    setBrand(b => ({...b, primaryColor:'#B8734A', sidebarColor:'#2B2B2B', bgColor:'#F7F2EB', textColor:'#2B2B2B', navTextColor:'#FFFFFF'}))
  }

  function handleLogo(e) {
    const file = e.target.files[0]
    if (!file) return
    setLogoFile(file)
    setLogoPreview(URL.createObjectURL(file))
  }

  function handleDocUpload(e) {
    const files = Array.from(e.target.files)
    const newDocs = files.map(f => ({ name: f.name, size: (f.size/1024).toFixed(0)+'KB', type: f.name.split('.').pop().toUpperCase(), date: 'Just now' }))
    setDocuments(d => [...d, ...newDocs])
  }

  function handleVideoUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setVideos(v => [...v, { title: file.name.replace(/\.[^.]+$/,''), source: 'upload', name: file.name, date: 'Just now' }])
  }

  function addVideoUrl() {
    if (!videoUrl.trim()) return
    setVideos(v => [...v, { title: videoTitle || videoUrl, source: 'url', url: videoUrl, date: 'Just now' }])
    setVideoUrl(''); setVideoTitle('')
  }
const { brand, saveBrand, setBrand } = useBrand()

  // Sync local state from loaded brand
  useEffect(() => {
    if (brand.companyName !== 'Demo Company Co.' || brand.primaryColor !== '#B8734A') {
      setCompanyName(brand.companyName)
      setMonogram(brand.monogram)
      setColor(brand.primaryColor)
      setSidebarColor(brand.sidebarColor || '#2B2B2B')
      setBgColor(brand.bgColor || '#F7F2EB')
      setTextColor(brand.textColor || '#2B2B2B')
      setNavTextColor(brand.navTextColor || '#FFFFFF')
      setTagline(brand.tagline)
      setIndustry(brand.industry || 'General Business')
      setLogoSize(brand.logoSize || 'medium')
      setLogoPreview(brand.logoUrl || null)
      if (brand.departments) setDepartments(brand.departments)
      if (brand.timeOffCategories) setTimeOffCategories(brand.timeOffCategories)
      if (brand.features) setFeatures(f => ({ ...f, ...brand.features }))
    }
  }, [brand])

  function applyTheme(t) {
    setColor(t.accent)
    setSidebarColor(t.sidebar)
    setBgColor(t.bg)
  }

  function handleSaveWithTheme() {
    saveBrand({
      companyName,
      monogram,
      primaryColor:      color,
      sidebarColor,
      bgColor,
      textColor,
      tagline,
      logoUrl:           logoPreview,
      logoSize,
      timeOffCategories,
      departments,
      features,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const slug = companyName.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')

  return (
    <div style={{fontFamily:"'Montserrat',sans-serif"}}>
      <div style={{marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <h1 style={{fontFamily:'Georgia,serif',fontSize:'1.4rem',color:'#2B2B2B',fontWeight:600}}>Client Configuration</h1>
          <p style={{fontSize:11,color:'#6B7D6B',marginTop:2}}>White-label settings — changes apply to the client portal instantly</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <span style={{padding:'4px 12px',borderRadius:20,fontSize:10,fontWeight:600,background:'rgba(184,115,74,0.1)',color:'#B8734A',border:'1px solid rgba(184,115,74,0.2)',alignSelf:'center'}}>GCW Admin</span>
          <button onClick={handleReset} style={{padding:'8px 18px',background:'#fff',border:'1px solid #EDE6DA',borderRadius:6,fontSize:12,fontWeight:600,color:'#6B7D6B',cursor:'pointer',fontFamily:"'Montserrat',sans-serif",marginRight:8}}>Reset Colors</button>
        <button onClick={handleSave} style={{padding:'8px 18px',background:'#B8734A',border:'none',borderRadius:6,color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
            Save & Publish →
          </button>
        </div>
      </div>

      {saved && (
        <div style={{display:'flex',alignItems:'center',gap:8,background:'rgba(74,124,89,0.1)',border:'1px solid #4A7C59',borderRadius:8,padding:'10px 14px',marginBottom:16}}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="#4A7C59" strokeWidth="1.5"/><path d="M5 8l2 2 4-4" stroke="#4A7C59" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <span style={{fontSize:12,color:'#4A7C59',fontWeight:500}}>Configuration saved and published to client portal.</span>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:16}}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>

          {/* Logo Upload */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Company Logo</div>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:'rgba(74,124,89,0.1)',color:'#4A7C59'}}>Replaces Monogram</span>
            </div>
            <div style={{padding:'16px',display:'flex',alignItems:'center',gap:20}}>
              <div style={{width:80,height:80,borderRadius:10,border:'2px dashed #EDE6DA',background:'#F7F2EB',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden',flexShrink:0}}>
                {logoPreview
                  ? <img src={logoPreview} alt="logo" style={{width:'100%',height:'100%',objectFit:'contain'}}/>
                  : <div style={{textAlign:'center'}}><div style={{fontSize:22,marginBottom:2}}>🖼</div><div style={{fontSize:9,color:'#8A9E8A'}}>No logo</div></div>
                }
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:500,color:'#2B2B2B',marginBottom:4}}>Upload your company logo</div>
                <div style={{fontSize:11,color:'#8A9E8A',marginBottom:10}}>PNG or SVG recommended. Transparent background works best. Max 2MB.</div>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={() => logoRef.current.click()} style={{padding:'7px 16px',background:'#B8734A',border:'none',borderRadius:6,color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
                    Upload Logo
                  </button>
                  {logoPreview && (
                    <button onClick={() => { setLogoPreview(null); setLogoFile(null); saveBrand({ logoUrl: null }) }} style={{padding:'7px 14px',background:'#fff',border:'1px solid #EDE6DA',borderRadius:6,color:'#8A9E8A',fontSize:12,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
                      Remove
                    </button>
                  )}
                </div>
                {logoFile && <div style={{fontSize:10,color:'#4A7C59',marginTop:6}}>✓ {logoFile.name} ready to save</div>}
                <input ref={logoRef} type="file" accept="image/*" onChange={handleLogo} style={{display:'none'}}/>
                <div style={{marginTop:12}}>
                  <div style={{fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:8}}>Logo Size</div>
                  <div style={{display:'flex',gap:8}}>
                    {['small','medium','large'].map(s => (
                      <button key={s} onClick={() => setLogoSize(s)}
                        style={{flex:1,padding:'6px',border:`1px solid ${logoSize===s?color:'#EDE6DA'}`,borderRadius:6,background:logoSize===s?color+'15':'#fff',color:logoSize===s?color:'#4A4A4A',fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif",textTransform:'capitalize'}}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Identity */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Company Identity</div>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:'rgba(74,124,89,0.1)',color:'#4A7C59'}}>Live</span>
            </div>
            <div style={{padding:'16px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div style={{gridColumn:'1/-1'}}>
                <label style={{display:'block',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:5}}>Company Name</label>
                <input value={companyName} onChange={e=>setCompanyName(e.target.value)} style={{width:'100%',padding:'9px 13px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:13,fontFamily:"'Montserrat',sans-serif",outline:'none',color:'#2B2B2B'}}/>
                <div style={{fontSize:10,color:'#8A9E8A',marginTop:4}}>Portal URL: collective-staff.vercel.app/{slug}</div>
              </div>
              <div>
                <label style={{display:'block',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:5}}>Monogram (fallback)</label>
                <input value={monogram} onChange={e=>setMonogram(e.target.value.slice(0,3).toUpperCase())} style={{width:'100%',padding:'9px 13px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:18,fontFamily:'Georgia,serif',fontWeight:700,outline:'none',color:'#2B2B2B',textAlign:'center',letterSpacing:'0.1em'}}/>
              </div>
              <div>
                <label style={{display:'block',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:5}}>Industry</label>
                <select value={industry} onChange={e=>setIndustry(e.target.value)} style={{width:'100%',padding:'9px 13px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:13,fontFamily:"'Montserrat',sans-serif",outline:'none',color:'#2B2B2B'}}>
                  {['General Business','Healthcare / Medical','Transportation','Retail','Hospitality','Construction','Education','Non-Profit'].map(o=>(
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div style={{gridColumn:'1/-1'}}>
                <label style={{display:'block',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:5}}>Tagline</label>
                <input value={tagline} onChange={e=>setTagline(e.target.value)} style={{width:'100%',padding:'9px 13px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:13,fontFamily:"'Montserrat',sans-serif",outline:'none',color:'#2B2B2B'}}/>
              </div>
            </div>
          </div>

{/* Theme */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Portal Theme</div>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:'rgba(74,124,89,0.1)',color:'#4A7C59'}}>Live Preview</span>
            </div>
            <div style={{padding:'16px'}}>

              {/* Theme packages */}
              <div style={{fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:10}}>Theme Packages</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:20}}>
                {themes.map(t => (
                  <div key={t.name} onClick={() => applyTheme(t)}
                    style={{border:`2px solid ${color===t.accent&&sidebarColor===t.sidebar?'#2B2B2B':'#EDE6DA'}`,borderRadius:8,overflow:'hidden',cursor:'pointer',transition:'all 0.15s'}}>
                    <div style={{display:'grid',gridTemplateColumns:'40% 60%',height:36}}>
                      <div style={{background:t.sidebar}}/>
                      <div style={{background:t.bg,display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <div style={{width:16,height:4,borderRadius:2,background:t.accent}}/>
                      </div>
                    </div>
                    <div style={{padding:'4px 8px',fontSize:10,fontWeight:600,color:'#2B2B2B',borderTop:'1px solid #EDE6DA',background:'#fff'}}>{t.name}</div>
                  </div>
                ))}
              </div>

              {/* Custom colors */}
              <div style={{fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:12}}>Custom Colors</div>
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {[
                  { label:'Accent / Button Color', value: color,        setter: (v) => { setColor(v); setBrand(b => ({...b, primaryColor:v})) },        hint:'Buttons, badges, active nav items' },
                  { label:'Sidebar Color',          value: sidebarColor, setter: (v) => { setSidebarColor(v); setBrand(b => ({...b, sidebarColor:v})) }, hint:'Navigation sidebar background' },
                  { label:'Background Color',       value: bgColor,      setter: (v) => { setBgColor(v); setBrand(b => ({...b, bgColor:v})) },      hint:'Main content area background' },
                  { label:'Text Color',             value: textColor,    setter: (v) => { setTextColor(v); setBrand(b => ({...b, textColor:v})) },    hint:'Main body text throughout portal' },
                  { label:'Heading Color',          value: textColor,    setter: (v) => { setTextColor(v); setBrand(b => ({...b, textColor:v})) },    hint:'Page titles and section headers' },
                  { label:'Nav Text Color',  value: navTextColor, setter: (v) => { setNavTextColor(v); setBrand(b => ({...b, navTextColor:v})) }, hint:'Text color in the navigation sidebar' },
                ].map(({ label, value, setter, hint }) => (
                  <div key={label} style={{display:'flex',alignItems:'center',gap:12}}>
                    <input type="color" value={value} onChange={e => setter(e.target.value)}
                      style={{width:36,height:36,borderRadius:6,border:'1px solid #EDE6DA',cursor:'pointer',padding:2,flexShrink:0}}/>
                    <input value={value} onChange={e => setter(e.target.value)}
                      style={{width:90,padding:'7px 10px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:12,fontFamily:"'Montserrat',sans-serif",outline:'none',color:'#2B2B2B'}}/>
                    <div>
                      <div style={{fontSize:11,fontWeight:600,color:'#2B2B2B'}}>{label}</div>
                      <div style={{fontSize:10,color:'#8A9E8A'}}>{hint}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Time Off Categories */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Time Off Categories</div>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:'rgba(107,125,107,0.08)',color:'#6B7D6B'}}>{timeOffCategories.length} categories</span>
            </div>
            <div>
              {timeOffCategories.map((c,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',borderBottom:'1px solid #EDE6DA'}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:color,flexShrink:0}}></div>
                  <span style={{flex:1,fontSize:13,color:'#4A4A4A'}}>{c}</span>
                  <button onClick={()=>removeCategory(i)} style={{background:'none',border:'none',fontSize:11,color:'#8A9E8A',cursor:'pointer',padding:'2px 6px'}}
                    onMouseEnter={e=>e.target.style.color='#A0392B'}
                    onMouseLeave={e=>e.target.style.color='#8A9E8A'}>Remove</button>
                </div>
              ))}
              <div style={{display:'flex',gap:8,padding:'10px 16px'}}>
                <input value={newCategory} onChange={e=>setNewCategory(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addCategory()} placeholder="Add category (e.g. Bereavement)..." style={{flex:1,padding:'7px 12px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:12,fontFamily:"'Montserrat',sans-serif",outline:'none'}}/>
                <button onClick={addCategory} style={{padding:'7px 14px',background:'#B8734A',border:'none',borderRadius:6,color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>+ Add</button>
              </div>
            </div>
          </div>

          {/* Departments */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Departments</div>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:'rgba(107,125,107,0.08)',color:'#6B7D6B'}}>{departments.length} configured</span>
            </div>
            <div>
              {departments.map((d,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',borderBottom:'1px solid #EDE6DA'}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:'#B8734A',flexShrink:0}}></div>
                  <span style={{flex:1,fontSize:13,color:'#4A4A4A'}}>{d}</span>
                  <button onClick={()=>removeDept(i)} style={{background:'none',border:'none',fontSize:11,color:'#8A9E8A',cursor:'pointer',padding:'2px 6px'}}
                    onMouseEnter={e=>e.target.style.color='#A0392B'}
                    onMouseLeave={e=>e.target.style.color='#8A9E8A'}>Remove</button>
                </div>
              ))}
              <div style={{display:'flex',gap:8,padding:'10px 16px'}}>
                <input value={newDept} onChange={e=>setNewDept(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addDept()} placeholder="Add department..." style={{flex:1,padding:'7px 12px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:12,fontFamily:"'Montserrat',sans-serif",outline:'none'}}/>
                <button onClick={addDept} style={{padding:'7px 14px',background:'#B8734A',border:'none',borderRadius:6,color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>+ Add</button>
              </div>
            </div>
          </div>

          {/* Document Upload */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Document Library Upload</div>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:'rgba(74,106,138,0.1)',color:'#4A6B8A'}}>{documents.length} files</span>
            </div>
            <div style={{padding:'16px'}}>
              <div style={{fontSize:11,color:'#8A9E8A',marginBottom:12}}>Documents uploaded here appear in the employee Document Library. SOPs, handbooks, forms, and policies.</div>
              <div onClick={() => docRef.current.click()} style={{border:'2px dashed #EDE6DA',borderRadius:8,padding:'24px',textAlign:'center',cursor:'pointer',transition:'all 0.2s',background:'#F7F2EB'}}
                onMouseEnter={e=>e.currentTarget.style.borderColor='#B8734A'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='#EDE6DA'}>
                <div style={{fontSize:28,marginBottom:6}}>📄</div>
                <div style={{fontSize:12,fontWeight:600,color:'#2B2B2B',marginBottom:2}}>Click to upload documents</div>
                <div style={{fontSize:11,color:'#8A9E8A'}}>PDF, DOC, DOCX · Multiple files supported</div>
                <input ref={docRef} type="file" accept=".pdf,.doc,.docx" multiple onChange={handleDocUpload} style={{display:'none'}}/>
              </div>
              {documents.length > 0 && (
                <div style={{marginTop:12,display:'flex',flexDirection:'column',gap:6}}>
                  {documents.map((d,i) => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'#F7F2EB',borderRadius:6,border:'1px solid #EDE6DA'}}>
                      <div style={{width:28,height:28,borderRadius:4,background:'#4A6B8A22',color:'#4A6B8A',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700,flexShrink:0}}>{d.type}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:500,color:'#2B2B2B',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{d.name}</div>
                        <div style={{fontSize:10,color:'#8A9E8A'}}>{d.size} · {d.date}</div>
                      </div>
                      <button onClick={()=>setDocuments(docs=>docs.filter((_,idx)=>idx!==i))} style={{background:'none',border:'none',color:'#8A9E8A',cursor:'pointer',fontSize:14,lineHeight:1}}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {documents.length > 0 && (
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:12,paddingTop:12,borderTop:'1px solid #EDE6DA'}}>
                  <span style={{fontSize:11,color:'#6B7D6B'}}>{documents.filter(d=>d.onboarding).length} added to onboarding · {documents.filter(d=>d.requireSig).length} require signature</span>
                  <button onClick={()=>alert('Documents saved to library!')} style={{padding:'8px 18px',background:'#4A7C59',border:'none',borderRadius:6,color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>Save to Library</button>
                </div>
              )}
            </div>
          </div>

          {/* Training Video Upload */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Training Videos</div>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:'rgba(124,94,167,0.1)',color:'#6B7D6B'}}>{videos.length} videos</span>
            </div>
            <div style={{padding:'16px'}}>
              <div style={{fontSize:11,color:'#8A9E8A',marginBottom:12}}>Videos appear in the employee Onboarding section. Upload a file or paste a YouTube/Vimeo link.</div>
              {/* URL input */}
              <div style={{background:'#F7F2EB',border:'1px solid #EDE6DA',borderRadius:8,padding:'12px',marginBottom:12}}>
                <div style={{fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,marginBottom:8}}>Paste Video URL (YouTube / Vimeo)</div>
                <input value={videoTitle} onChange={e=>setVideoTitle(e.target.value)} placeholder="Video title (optional)..." style={{width:'100%',padding:'7px 12px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:12,fontFamily:"'Montserrat',sans-serif",outline:'none',marginBottom:6,boxSizing:'border-box'}}/>
                <div style={{display:'flex',gap:8}}>
                  <input value={videoUrl} onChange={e=>setVideoUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." style={{flex:1,padding:'7px 12px',border:'1px solid #EDE6DA',borderRadius:6,fontSize:12,fontFamily:"'Montserrat',sans-serif",outline:'none'}}/>
                  <button onClick={addVideoUrl} disabled={!videoUrl.trim()} style={{padding:'7px 14px',background:videoUrl.trim()?'#6B7D6B':'#EDE6DA',border:'none',borderRadius:6,color:videoUrl.trim()?'#fff':'#8A9E8A',fontSize:12,fontWeight:600,cursor:videoUrl.trim()?'pointer':'default',fontFamily:"'Montserrat',sans-serif",whiteSpace:'nowrap'}}>+ Add</button>
                </div>
              </div>
              {/* File upload */}
              <div onClick={() => videoRef.current.click()} style={{border:'2px dashed #EDE6DA',borderRadius:8,padding:'16px',textAlign:'center',cursor:'pointer',transition:'all 0.2s',background:'#F7F2EB'}}
                onMouseEnter={e=>e.currentTarget.style.borderColor='#6B7D6B'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='#EDE6DA'}>
                <div style={{fontSize:24,marginBottom:4}}>🎬</div>
                <div style={{fontSize:12,fontWeight:600,color:'#2B2B2B',marginBottom:2}}>Or upload a video file</div>
                <div style={{fontSize:11,color:'#8A9E8A'}}>MP4, MOV · Max 500MB</div>
                <input ref={videoRef} type="file" accept="video/*" onChange={handleVideoUpload} style={{display:'none'}}/>
              </div>
              {videos.length > 0 && (
                <div style={{marginTop:12,display:'flex',flexDirection:'column',gap:6}}>
                  {videos.map((v,i) => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'#F7F2EB',borderRadius:6,border:'1px solid #EDE6DA'}}>
                      <div style={{width:28,height:28,borderRadius:4,background:'#6B7D6B22',color:'#6B7D6B',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,flexShrink:0}}>▶</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:500,color:'#2B2B2B',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{v.title}</div>
                        <div style={{fontSize:10,color:'#8A9E8A'}}>{v.source === 'url' ? 'External link' : 'Uploaded file'} · {v.date}</div>
                      </div>
                      <button onClick={()=>setVideos(vids=>vids.filter((_,idx)=>idx!==i))} style={{background:'none',border:'none',color:'#8A9E8A',cursor:'pointer',fontSize:14,lineHeight:1}}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Feature Toggles */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Portal Features</div>
            </div>
            <div style={{padding:'4px 0'}}>
              {[
                {key:'onboarding',  label:'Employee Onboarding Flow',   sub:'New hire checklist, training modules, e-signature'},
                {key:'policies',    label:'Signed Documents',            sub:'Require employee acknowledgment on documents'},
                {key:'timesheets',  label:'Timesheet Submission',        sub:'Weekly timesheet entry and manager approval'},
                {key:'timeOff',     label:'Time Off Requests',           sub:'Configurable categories — set above'},
                {key:'memos',       label:'Memos & Announcements',       sub:'Internal messaging with read receipts'},
                {key:'incidents',   label:'Incident Reports',            sub:'Vehicle, workplace, and safety incidents'},
                {key:'performance', label:'Performance Tracking',        sub:'Reviews, ratings, warnings, and recognition'},
                {key:'collectiveServices', price:'+$29/mo', label:'Collective Services', desc:'IT & Facilities helpdesk, change management, work logs'},
                  {key:'roomScheduler', price:'+$19/mo', label:'Collective Campus',         sub:'Space & room booking — housing, conference rooms, dining'},
              ].map(f=>(
                <div key={f.key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',borderBottom:'1px solid #EDE6DA'}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:500,color:'#2B2B2B'}}>{f.label}</div>
                    <div style={{fontSize:11,color:'#8A9E8A',marginTop:2}}>{f.sub}</div>
                  </div>
                  <div onClick={()=>toggleFeature(f.key)} style={{width:38,height:22,borderRadius:11,background:features[f.key]?color:'#EDE6DA',cursor:'pointer',position:'relative',transition:'background 0.2s',flexShrink:0,marginLeft:16}}>
                    <div style={{width:18,height:18,borderRadius:'50%',background:'#fff',position:'absolute',top:2,left:features[f.key]?18:2,transition:'left 0.2s'}}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Live Preview */}
        <div style={{display:'flex',flexDirection:'column',gap:14,position:'sticky',top:20}}>
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Live Preview</div>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:'rgba(74,124,89,0.1)',color:'#4A7C59'}}>Updates instantly</span>
            </div>
            <div style={{padding:'12px'}}>
              <div style={{background:'#2B2B2B',borderRadius:8,overflow:'hidden'}}>
                <div style={{padding:'6px 10px',display:'flex',alignItems:'center',gap:6}}>
                  <div style={{display:'flex',gap:4}}>
                    {['#E05252','#E0A752','#52C152'].map(c=><div key={c} style={{width:7,height:7,borderRadius:'50%',background:c}}/>)}
                  </div>
                  <div style={{flex:1,background:'rgba(255,255,255,0.1)',borderRadius:3,padding:'2px 8px',fontSize:9,color:'rgba(255,255,255,0.5)',fontFamily:"'Montserrat',sans-serif"}}>
                    collective-staff.vercel.app/{slug}
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',height:200}}>
                  <div style={{background:sidebarColor,padding:'16px',display:'flex',flexDirection:'column',justifyContent:'center'}}>
                    {logoPreview
                      ? <img src={logoPreview} alt="logo" style={{width:logoSize==='large'?56:logoSize==='small'?28:40,height:logoSize==='large'?56:logoSize==='small'?28:40,objectFit:'contain',marginBottom:8,borderRadius:4}}/>
                      : <div style={{width:28,height:28,borderRadius:'50%',border:`1.5px solid ${color}`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'Georgia,serif',fontSize:10,fontWeight:700,color,marginBottom:8}}>{monogram}</div>
                    }
                    <div style={{fontFamily:'Georgia,serif',fontSize:13,color:'#fff',fontWeight:600,lineHeight:1.2,marginBottom:4}}>Collective<br/>Staff</div>
                    <div style={{fontSize:8,color:'rgba(255,255,255,0.35)',letterSpacing:'0.1em',textTransform:'uppercase'}}>{tagline.slice(0,20)}...</div>
                  </div>
                  <div style={{background:bgColor,padding:'16px',display:'flex',flexDirection:'column',justifyContent:'center'}}>
                    <div style={{fontSize:8,color,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:4,fontWeight:600}}>Employee Portal</div>
                    <div style={{fontFamily:'Georgia,serif',fontSize:13,color:'#2B2B2B',marginBottom:10}}>Welcome back.</div>
                    <div style={{height:18,background:'#EDE6DA',borderRadius:3,marginBottom:5}}/>
                    <div style={{height:18,background:'#EDE6DA',borderRadius:3,marginBottom:8}}/>
                    <div style={{height:20,background:color,borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <span style={{fontSize:8,color:'#fff',fontWeight:600}}>Sign In</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Sidebar Preview</div>
            </div>
            <div style={{padding:'12px'}}>
              <div style={{background:sidebarColor,borderRadius:8,padding:'12px'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,paddingBottom:8,borderBottom:'1px solid rgba(255,255,255,0.08)',marginBottom:8}}>
                  {logoPreview
                    ? <img src={logoPreview} alt="logo" style={{width:22,height:22,objectFit:'contain',borderRadius:3}}/>
                    : <div style={{width:22,height:22,borderRadius:'50%',border:`1px solid ${color}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700,color}}>{monogram}</div>
                  }
                  <div>
                    <div style={{fontFamily:'Georgia,serif',fontSize:11,color:'#fff',fontWeight:600}}>Collective Staff</div>
                    <div style={{fontSize:8,color:'rgba(255,255,255,0.35)',textTransform:'uppercase',letterSpacing:'0.1em'}}>{companyName.slice(0,20)}</div>
                  </div>
                </div>
                {['Dashboard','Onboarding','Timesheets','Memos'].map((item,i)=>(
                  <div key={item} style={{padding:'5px 6px',borderRadius:4,fontSize:10,color:i===0?'#fff':'rgba(255,255,255,0.4)',background:i===0?color+'22':'transparent',borderLeft:i===0?`2px solid ${color}`:'2px solid transparent',marginBottom:2}}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Plan & Billing</div>
            </div>
            <div style={{padding:'14px 16px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                <span style={{fontSize:12,color:'#4A4A4A',fontWeight:500}}>Current Plan</span>
                <span style={{fontSize:10,padding:'2px 8px',borderRadius:8,fontWeight:600,background:'rgba(184,115,74,0.1)',color:'#B8734A'}}>Professional</span>
              </div>
              <div style={{fontSize:11,color:'#8A9E8A',marginBottom:12}}>Up to 50 employees · All features · Priority support</div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                <span style={{fontSize:12,color:'#4A4A4A'}}>Active employees</span>
                <span style={{fontFamily:'Georgia,serif',fontSize:16,color,fontWeight:600}}>18 <span style={{fontSize:11,color:'#8A9E8A',fontFamily:"'Montserrat',sans-serif",fontWeight:400}}>/ 50</span></span>
              </div>
              <div style={{height:5,background:'#EDE6DA',borderRadius:3,overflow:'hidden'}}>
                <div style={{height:'100%',width:'36%',background:color,borderRadius:3}}></div>
              </div>
              <div style={{fontSize:10,color:'#8A9E8A',marginTop:4}}>36% capacity used</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
